import { useState, useRef } from "react";
import { post, get } from "../api";
import { fmt } from "../theme";

/* ═══════════════════════════════════════════════════
   ImportarProdutos.jsx — Importação de produtos
   → mixup-lojista/src/pages/ImportarProdutos.jsx
   
   3 métodos: Excel/CSV, iFood, Cadastro Rápido
   Props: lojaId, onImported (callback), onClose
═══════════════════════════════════════════════════ */

const DC={
  bg:"#0f1117",bgSoft:"#161923",card:"#1c1f2e",
  border:"#2a2e3f",
  accent:"#E8201A",accentSoft:"#E8201A20",
  green:"#22C55E",greenSoft:"#22C55E18",
  blue:"#3B82F6",blueSoft:"#3B82F618",
  orange:"#F97316",orangeSoft:"#F9731618",
  purple:"#8B5CF6",
  text:"#E8E9ED",textSoft:"#9CA3B4",textMuted:"#5C6378",white:"#FFFFFF",
};
const mono="'JetBrains Mono',monospace";
const sInput={padding:"10px 14px",borderRadius:8,border:`1px solid ${DC.border}`,background:DC.bgSoft,color:DC.text,fontSize:13,outline:"none",fontFamily:"inherit",boxSizing:"border-box",width:"100%"};
const sBtn=(bg,cor="#fff")=>({padding:"12px 20px",borderRadius:8,border:"none",fontWeight:700,fontSize:14,cursor:"pointer",background:bg,color:cor,transition:"all .12s"});

const API_BASE = window.location.origin.includes("localhost") 
  ? "https://mixupbr.com.br/api" 
  : window.location.origin.replace(/\/mixup-lojista.*/, "") + "/api";

export default function ImportarProdutos({ lojaId, onImported, onClose }) {
  const [aba, setAba] = useState("excel");
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [toast, setToast] = useState(null);
  const notify = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3000); };

  // ═══ EXCEL/CSV ═══
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!["xlsx", "csv", "tsv"].includes(ext)) {
      notify("Use arquivo .xlsx ou .csv", false);
      return;
    }
    setFileName(file.name);
    setLoading(true);
    setResultado(null);

    const form = new FormData();
    form.append("file", file);
    form.append("acao", ext === "xlsx" ? "upload_excel" : "upload_csv");
    form.append("loja_id", lojaId || 1);

    try {
      const resp = await fetch(API_BASE + "/importar_produtos/", {
        method: "POST",
        body: form,
      });
      const r = await resp.json();
      if (r.ok) {
        setResultado(r);
        notify(`✅ ${r.criados} criados, ${r.atualizados} atualizados`);
        if (onImported) onImported();
      } else {
        notify(r.erro || "Erro na importação", false);
      }
    } catch (e) {
      notify("Erro: " + e.message, false);
    }
    setLoading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };

  // ═══ IFOOD ═══
  const [ifoodUrl, setIfoodUrl] = useState("");
  const [ifoodPreview, setIfoodPreview] = useState(null);
  const [ifoodSelAll, setIfoodSelAll] = useState(true);
  const [ifoodSel, setIfoodSel] = useState({});

  const buscarIfood = async () => {
    if (!ifoodUrl.trim()) { notify("Cole a URL do iFood", false); return; }
    setLoading(true);
    setIfoodPreview(null);
    try {
      const r = await post("importar_produtos", { acao: "ifood", url: ifoodUrl, loja_id: lojaId || 1 });
      if (r.ok && r.produtos) {
        setIfoodPreview(r);
        const sel = {};
        r.produtos.forEach((p, i) => { sel[i] = true; });
        setIfoodSel(sel);
        setIfoodSelAll(true);
        notify(`${r.total} produtos encontrados!`);
      } else {
        notify(r.erro || "Nenhum produto encontrado", false);
      }
    } catch (e) {
      notify("Erro: " + e.message, false);
    }
    setLoading(false);
  };

  const importarIfood = async () => {
    if (!ifoodPreview) return;
    const selecionados = ifoodPreview.produtos.filter((_, i) => ifoodSel[i]);
    if (selecionados.length === 0) { notify("Selecione ao menos 1 produto", false); return; }

    setLoading(true);
    try {
      const r = await post("importar_produtos", { acao: "importar_json", loja_id: lojaId || 1, produtos: selecionados });
      if (r.ok) {
        setResultado(r);
        notify(`✅ ${r.criados} criados, ${r.atualizados} atualizados`);
        setIfoodPreview(null);
        if (onImported) onImported();
      } else {
        notify(r.erro || "Erro", false);
      }
    } catch (e) {
      notify("Erro: " + e.message, false);
    }
    setLoading(false);
  };

  // ═══ CADASTRO RÁPIDO ═══
  const [rapido, setRapido] = useState({ nome: "", categoria: "", preco: "", codigo: "", descricao: "" });
  const [rapCategorias, setRapCategorias] = useState([]);
  const nomeRef = useRef(null);

  const cadastrarRapido = async () => {
    if (!rapido.nome.trim()) { notify("Nome obrigatório", false); return; }
    if (!rapido.preco) { notify("Preço obrigatório", false); return; }
    setLoading(true);
    try {
      const r = await post("importar_produtos", { acao: "rapido", loja_id: lojaId || 1, ...rapido });
      if (r.ok) {
        notify(`✅ ${r.nome} cadastrado!`);
        if (!rapCategorias.includes(rapido.categoria) && rapido.categoria) {
          setRapCategorias([...rapCategorias, rapido.categoria]);
        }
        setRapido({ nome: "", categoria: rapido.categoria, preco: "", codigo: "", descricao: "" });
        setTimeout(() => nomeRef.current?.focus(), 100);
        if (onImported) onImported();
      } else {
        notify(r.erro || "Erro", false);
      }
    } catch (e) {
      notify("Erro: " + e.message, false);
    }
    setLoading(false);
  };

  // ═══ FOTO DO CARDÁPIO (Gemini Vision) ═══
  const fotoRef = useRef(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoProdutos, setFotoProdutos] = useState([]);
  const [fotoSel, setFotoSel] = useState({});
  const [fotoSelAll, setFotoSelAll] = useState(true);
  const [fotoDragOver, setFotoDragOver] = useState(false);

  const enviarFoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      notify("Envie uma imagem (JPG, PNG)", false);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      notify("Imagem muito grande. Máximo 10MB", false);
      return;
    }
    setLoading(true);
    setFotoProdutos([]);
    setResultado(null);
    
    // Mostra preview
    const reader = new FileReader();
    reader.onload = (e) => setFotoPreview(e.target.result);
    reader.readAsDataURL(file);
    
    const form = new FormData();
    form.append("file", file);
    form.append("acao", "foto");
    form.append("loja_id", lojaId || 1);
    
    try {
      const resp = await fetch(API_BASE + "/importar_produtos/", { method: "POST", body: form });
      const r = await resp.json();
      if (r.ok && r.produtos) {
        setFotoProdutos(r.produtos);
        const sel = {};
        r.produtos.forEach((_, i) => { sel[i] = true; });
        setFotoSel(sel);
        setFotoSelAll(true);
        notify(`✨ ${r.total} produtos identificados!`);
      } else {
        notify(r.erro || "Erro ao processar imagem", false);
      }
    } catch (e) {
      notify("Erro: " + e.message, false);
    }
    setLoading(false);
  };

  const handleFotoDrop = (e) => {
    e.preventDefault();
    setFotoDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) enviarFoto(file);
  };

  const togglerFotoAll = () => {
    const novo = !fotoSelAll;
    setFotoSelAll(novo);
    const sel = {};
    fotoProdutos.forEach((_, i) => { sel[i] = novo; });
    setFotoSel(sel);
  };

  const editarFotoProduto = (i, campo, valor) => {
    const novos = [...fotoProdutos];
    novos[i] = { ...novos[i], [campo]: valor };
    setFotoProdutos(novos);
  };

  const cadastrarFotoProdutos = async () => {
    const selecionados = fotoProdutos.filter((_, i) => fotoSel[i]);
    if (!selecionados.length) {
      notify("Selecione ao menos 1 produto", false);
      return;
    }
    setLoading(true);
    try {
      const r = await post("importar_produtos", { acao: "importar_json", loja_id: lojaId || 1, produtos: selecionados });
      if (r.ok) {
        setResultado(r);
        setFotoProdutos([]);
        setFotoPreview(null);
        notify(`✅ ${r.criados} criados, ${r.atualizados} atualizados`);
        if (onImported) onImported();
      } else {
        notify(r.erro || "Erro", false);
      }
    } catch (e) {
      notify("Erro: " + e.message, false);
    }
    setLoading(false);
  };

  // ═══ RENDER ═══
  const ABAS = [
    { k: "excel", l: "📋 Planilha Excel", cor: DC.green },
    { k: "foto", l: "📸 Foto do Cardápio", cor: DC.purple },
    { k: "ifood", l: "🔗 Importar do iFood", cor: DC.orange },
    { k: "rapido", l: "⚡ Cadastro Rápido", cor: DC.blue },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: DC.bg, color: DC.text }}>
      {toast && <div style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 9999, background: toast.ok ? DC.green : DC.accent, color: "#fff", padding: "10px 28px", borderRadius: 10, fontWeight: 700, fontSize: 14 }}>{toast.msg}</div>}

      {/* Header */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${DC.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: DC.bgSoft }}>
        <div style={{ fontSize: 20, fontWeight: 800 }}>📦 Importar Produtos</div>
        {onClose && <div onClick={onClose} style={{ cursor: "pointer", fontSize: 20, color: DC.textMuted }}>✕</div>}
      </div>

      {/* Abas */}
      <div style={{ display: "flex", borderBottom: `1px solid ${DC.border}`, background: DC.bgSoft }}>
        {ABAS.map(a => (
          <div key={a.k} onClick={() => { setAba(a.k); setResultado(null); }}
            style={{ flex: 1, padding: "12px", textAlign: "center", fontSize: 14, fontWeight: aba === a.k ? 700 : 500, color: aba === a.k ? DC.text : DC.textMuted, borderBottom: aba === a.k ? `3px solid ${a.cor}` : "3px solid transparent", cursor: "pointer" }}>
            {a.l}
          </div>
        ))}
      </div>

      {/* Conteúdo */}
      <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>

        {/* ════ EXCEL/CSV ════ */}
        {aba === "excel" && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>1. Baixe o template</div>
              <a href={API_BASE.replace("/api", "") + "/mixup-lojista/template_produtos_mixup.xlsx"}
                download style={{ ...sBtn(DC.green), textDecoration: "none", display: "inline-block" }}>
                📥 Baixar Template Excel
              </a>
              <span style={{ fontSize: 12, color: DC.textMuted, marginLeft: 12 }}>Preencha e salve como .xlsx ou .csv</span>
            </div>

            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>2. Faça upload do arquivo preenchido</div>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? DC.green : DC.border}`, borderRadius: 16,
                padding: 40, textAlign: "center", cursor: "pointer",
                background: dragOver ? DC.greenSoft : DC.card, transition: "all .2s",
              }}>
              <input ref={fileRef} type="file" accept=".xlsx,.csv,.tsv" style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); }} />
              <div style={{ fontSize: 40, marginBottom: 12 }}>📁</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: DC.text }}>
                {loading ? "Importando..." : fileName || "Arraste o arquivo aqui ou clique para selecionar"}
              </div>
              <div style={{ fontSize: 12, color: DC.textMuted, marginTop: 6 }}>.xlsx ou .csv · Máx 1000 produtos</div>
            </div>

            {resultado && (
              <div style={{ marginTop: 20, padding: 20, borderRadius: 12, background: DC.card, border: `1px solid ${DC.border}` }}>
                <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12, color: DC.green }}>✅ Importação concluída</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <Stat label="Criados" value={resultado.criados} cor={DC.green} />
                  <Stat label="Atualizados" value={resultado.atualizados} cor={DC.blue} />
                  <Stat label="Erros" value={resultado.erros} cor={resultado.erros > 0 ? DC.accent : DC.textMuted} />
                </div>
                {resultado.erros > 0 && Array.isArray(resultado.mensagens_erro) && resultado.mensagens_erro.length > 0 && (
                  <div style={{ marginTop: 16, padding: 12, borderRadius: 8, background: DC.accentSoft, border: `1px solid ${DC.accent}55` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: DC.accent, marginBottom: 8 }}>Detalhes dos erros:</div>
                    {resultado.mensagens_erro.map((msg, i) => (
                      <div key={i} style={{ fontSize: 11, color: DC.text, fontFamily: mono, marginBottom: 4 }}>• {msg}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ════ FOTO DO CARDÁPIO ════ */}
        {aba === "foto" && (
          <div>
            <div style={{ fontSize: 13, color: DC.textSoft, marginBottom: 16 }}>
              📸 Tire uma foto do cardápio ou catálogo. A IA identifica os produtos automaticamente. Você pode revisar e editar antes de cadastrar.
            </div>
            
            {!fotoProdutos.length && (
              <div onClick={() => fotoRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setFotoDragOver(true); }}
                onDragLeave={() => setFotoDragOver(false)}
                onDrop={handleFotoDrop}
                style={{ border: `2px dashed ${fotoDragOver ? DC.purple : DC.border}`, borderRadius: 12, padding: 60, textAlign: "center", cursor: "pointer", background: fotoDragOver ? DC.purple + "10" : DC.card, transition: "all .15s" }}>
                <input ref={fotoRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(e) => enviarFoto(e.target.files?.[0])} />
                {loading ? (
                  <div>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🤖</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: DC.purple }}>Analisando imagem...</div>
                    <div style={{ fontSize: 12, color: DC.textMuted, marginTop: 8 }}>Pode levar até 30 segundos</div>
                  </div>
                ) : fotoPreview ? (
                  <div>
                    <img src={fotoPreview} alt="" style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 8, marginBottom: 12 }} />
                    <div style={{ fontSize: 14, color: DC.textSoft }}>Clique para enviar outra foto</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 56, marginBottom: 12 }}>📸</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: DC.text, marginBottom: 4 }}>Tire ou envie foto do cardápio</div>
                    <div style={{ fontSize: 12, color: DC.textMuted }}>JPG ou PNG · Máx 10MB</div>
                  </div>
                )}
              </div>
            )}
            
            {fotoProdutos.length > 0 && (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, padding: 12, background: DC.purple + "15", borderRadius: 8, border: `1px solid ${DC.purple}40` }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: DC.purple }}>✨ {fotoProdutos.length} produtos identificados</div>
                    <div style={{ fontSize: 11, color: DC.textMuted, marginTop: 2 }}>Revise, edite se necessário e confirme</div>
                  </div>
                  <button onClick={togglerFotoAll} style={{ ...sBtn(DC.card, DC.text), padding: "8px 14px", fontSize: 12, border: `1px solid ${DC.border}` }}>
                    {fotoSelAll ? "Desmarcar todos" : "Marcar todos"}
                  </button>
                </div>
                
                <div style={{ maxHeight: 500, overflowY: "auto", border: `1px solid ${DC.border}`, borderRadius: 8 }}>
                  {fotoProdutos.map((p, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "30px 2fr 1.2fr 100px 30px", gap: 8, padding: 10, borderBottom: i < fotoProdutos.length - 1 ? `1px solid ${DC.border}` : "none", alignItems: "center", background: fotoSel[i] ? "transparent" : DC.bgSoft }}>
                      <input type="checkbox" checked={!!fotoSel[i]} onChange={() => setFotoSel({ ...fotoSel, [i]: !fotoSel[i] })} style={{ cursor: "pointer", width: 16, height: 16, accentColor: DC.purple }} />
                      <input value={p.nome || ""} onChange={(e) => editarFotoProduto(i, "nome", e.target.value)} style={{ ...sInput, padding: "6px 10px", fontSize: 13 }} placeholder="Nome" />
                      <input value={p.categoria || ""} onChange={(e) => editarFotoProduto(i, "categoria", e.target.value)} style={{ ...sInput, padding: "6px 10px", fontSize: 13 }} placeholder="Categoria" />
                      <input type="number" step="0.01" value={p.preco || ""} onChange={(e) => editarFotoProduto(i, "preco", e.target.value)} style={{ ...sInput, padding: "6px 10px", fontSize: 13, textAlign: "right", fontFamily: mono }} placeholder="0,00" />
                      <span style={{ fontSize: 18, color: DC.green, textAlign: "center" }}>{fotoSel[i] ? "✓" : ""}</span>
                    </div>
                  ))}
                </div>
                
                <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                  <button onClick={() => { setFotoProdutos([]); setFotoPreview(null); }} style={{ ...sBtn(DC.card, DC.textSoft), border: `1px solid ${DC.border}`, padding: "12px 20px" }}>
                    ← Tirar outra foto
                  </button>
                  <button onClick={cadastrarFotoProdutos} disabled={loading} style={{ ...sBtn(DC.green), flex: 1, padding: "12px 20px", opacity: loading ? 0.6 : 1 }}>
                    {loading ? "Cadastrando..." : `✅ Cadastrar ${Object.values(fotoSel).filter(Boolean).length} produtos`}
                  </button>
                </div>
              </div>
            )}
            
            {resultado && (
              <div style={{ marginTop: 20, padding: 20, borderRadius: 12, background: DC.card, border: `1px solid ${DC.border}` }}>
                <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12, color: DC.green }}>✅ Cadastro concluído</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <Stat label="Criados" value={resultado.criados} cor={DC.green} />
                  <Stat label="Atualizados" value={resultado.atualizados} cor={DC.blue} />
                  <Stat label="Erros" value={resultado.erros} cor={resultado.erros > 0 ? DC.accent : DC.textMuted} />
                </div>
                {resultado.erros > 0 && Array.isArray(resultado.mensagens_erro) && resultado.mensagens_erro.length > 0 && (
                  <div style={{ marginTop: 16, padding: 12, borderRadius: 8, background: DC.accentSoft, border: `1px solid ${DC.accent}55` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: DC.accent, marginBottom: 8 }}>Detalhes dos erros:</div>
                    {resultado.mensagens_erro.map((msg, i) => (
                      <div key={i} style={{ fontSize: 11, color: DC.text, fontFamily: mono, marginBottom: 4 }}>• {msg}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ════ IFOOD ════ */}
        {aba === "ifood" && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Cole a URL da loja no iFood</div>
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              <input value={ifoodUrl} onChange={e => setIfoodUrl(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") buscarIfood(); }}
                placeholder="https://www.ifood.com.br/delivery/cidade/nome-loja/uuid"
                style={{ ...sInput, flex: 1 }} />
              <button onClick={buscarIfood} disabled={loading}
                style={{ ...sBtn(DC.orange), opacity: loading ? 0.6 : 1, whiteSpace: "nowrap" }}>
                {loading ? "Buscando..." : "🔍 Buscar"}
              </button>
            </div>

            <div style={{ fontSize: 11, color: DC.textMuted, marginBottom: 16, padding: "8px 12px", borderRadius: 8, background: DC.card, border: `1px solid ${DC.border}` }}>
              💡 <b>Como encontrar:</b> Abra o iFood → busque a loja → copie a URL da barra de endereços do navegador
            </div>

            {/* Preview */}
            {ifoodPreview && (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{ifoodPreview.total} produtos encontrados</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <label style={{ fontSize: 12, color: DC.textMuted, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                      <input type="checkbox" checked={ifoodSelAll} onChange={e => {
                        setIfoodSelAll(e.target.checked);
                        const s = {};
                        ifoodPreview.produtos.forEach((_, i) => { s[i] = e.target.checked; });
                        setIfoodSel(s);
                      }} /> Selecionar todos
                    </label>
                    <button onClick={importarIfood} disabled={loading}
                      style={{ ...sBtn(DC.green), padding: "8px 16px", fontSize: 13, opacity: loading ? 0.6 : 1 }}>
                      {loading ? "Importando..." : `✅ Importar ${Object.values(ifoodSel).filter(Boolean).length} produtos`}
                    </button>
                  </div>
                </div>

                <div style={{ maxHeight: 400, overflowY: "auto", borderRadius: 10, border: `1px solid ${DC.border}` }}>
                  {ifoodPreview.produtos.map((p, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: `1px solid ${DC.border}20`, background: ifoodSel[i] ? DC.greenSoft : "transparent", cursor: "pointer" }}
                      onClick={() => setIfoodSel(s => ({ ...s, [i]: !s[i] }))}>
                      <input type="checkbox" checked={!!ifoodSel[i]} readOnly />
                      {p.imagem && <img src={p.imagem} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover" }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.nome}</div>
                        <div style={{ fontSize: 11, color: DC.textMuted }}>{p.categoria}</div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: DC.green, fontFamily: mono }}>{fmt(p.preco)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {resultado && (
              <div style={{ marginTop: 16, padding: 16, borderRadius: 12, background: DC.card, border: `1px solid ${DC.border}` }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: DC.green }}>✅ {resultado.mensagem}</div>
              </div>
            )}
          </div>
        )}

        {/* ════ CADASTRO RÁPIDO ════ */}
        {aba === "rapido" && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Cadastro rápido — preencha e tecle Enter</div>

            <div style={{ background: DC.card, borderRadius: 12, padding: 20, border: `1px solid ${DC.border}`, marginBottom: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: DC.textMuted, marginBottom: 4 }}>Nome *</div>
                  <input ref={nomeRef} value={rapido.nome} onChange={e => setRapido(r => ({ ...r, nome: e.target.value }))}
                    onKeyDown={e => { if (e.key === "Enter") cadastrarRapido(); }}
                    style={sInput} placeholder="Nome do produto" autoFocus />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: DC.textMuted, marginBottom: 4 }}>Categoria *</div>
                  <input value={rapido.categoria} onChange={e => setRapido(r => ({ ...r, categoria: e.target.value }))}
                    onKeyDown={e => { if (e.key === "Enter") cadastrarRapido(); }}
                    list="cat-list" style={sInput} placeholder="Lanches, Bebidas..." />
                  <datalist id="cat-list">
                    {rapCategorias.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: DC.textMuted, marginBottom: 4 }}>Preço (R$) *</div>
                  <input value={rapido.preco} onChange={e => setRapido(r => ({ ...r, preco: e.target.value.replace(/[^\d.,]/g, "") }))}
                    onKeyDown={e => { if (e.key === "Enter") cadastrarRapido(); }}
                    style={{ ...sInput, fontFamily: mono, fontSize: 16, fontWeight: 800, color: DC.green }} placeholder="0,00" />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: DC.textMuted, marginBottom: 4 }}>Código</div>
                  <input value={rapido.codigo} onChange={e => setRapido(r => ({ ...r, codigo: e.target.value }))}
                    onKeyDown={e => { if (e.key === "Enter") cadastrarRapido(); }}
                    style={sInput} placeholder="SKU001" />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: DC.textMuted, marginBottom: 4 }}>Descrição</div>
                  <input value={rapido.descricao} onChange={e => setRapido(r => ({ ...r, descricao: e.target.value }))}
                    onKeyDown={e => { if (e.key === "Enter") cadastrarRapido(); }}
                    style={sInput} placeholder="Opcional" />
                </div>
              </div>
              <button onClick={cadastrarRapido} disabled={loading}
                style={{ ...sBtn(DC.green), width: "100%", padding: 14, fontSize: 15, borderRadius: 10, opacity: loading ? 0.6 : 1 }}>
                {loading ? "Salvando..." : "Enter — Cadastrar e próximo"}
              </button>
            </div>

            <div style={{ fontSize: 11, color: DC.textMuted, padding: "10px 14px", borderRadius: 8, background: DC.card, border: `1px solid ${DC.border}` }}>
              💡 A categoria é lembrada — cadastre todos de uma categoria, depois mude pra próxima. O cursor volta pro nome automaticamente.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, cor }) {
  return (
    <div style={{ background: DC.bgSoft, borderRadius: 8, padding: 12, textAlign: "center", border: `1px solid ${DC.border}` }}>
      <div style={{ fontSize: 10, color: DC.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: cor, fontFamily: mono }}>{value}</div>
    </div>
  );
}
